import { recalculateAllQuoteOrderRows } from '@teinor/erp/prices/quote/calculate'
import { SellVariableTypeInversed, SellVariableTypeKeys } from '@teinor/erp/types/company/config/sellVariable'
import { useMemo, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { ButtonSecondary } from '../../../../../../../baseComponents/Button'
import { UserPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import { ModalBigCentered } from '../../../../../../../baseComponents/Modal'
import { ModalOpenedSetTy } from '../../../../../../../baseComponents/Modal/types'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { InputTableLeftColumnChange } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { OrderHeaderURLs, InputTableLayoutData } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import TableWithoutVirtualListing from '../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { DeleteRow } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { CustomSelectWithSearchBar } from '../../../../../../../forms/CustomSelect'
import FormRenderer from '../../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { InputNumberWithLabelMarginTextRightDebounce, InputWithLabelMargin } from '../../../../../../../forms/Input'
import InputPrice from '../../../../../../../forms/Input/InputPrice'
import { TextField } from '../../../../../../../forms/TextField'
import { twinFetchPostJSON, twinFetchPostText } from '../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getSellVariables, getAllCurrencies } from '../../../../../../../utils/reducers/getters'
import { updateVariableQuantityScaled } from '../../../OrderTable/TableComponents/ModalsOrderTable/ModalEditAddonProductOrder/functions'
import { SetCompleteDataTy } from '../../../types'

interface ModalVariableProps extends ModalEditComponentProps {
    isReadOnly?: boolean
    orderHeaderURLs: OrderHeaderURLs
    updateHeaderData: string
    headerData: TwinDictionary
    getCompleteData: () => void
    userPermissions: UserPermissions
    setCompleteData: SetCompleteDataTy
}

const ModalVariablesOrder: React.FC<ModalVariableProps> = ({ isReadOnly, ...rest }) => {
    if (isReadOnly) {
        return <ModalVariablesOrderReadOnly {...{ isReadOnly, ...rest }} />
    }
    return (<ModalVariablesOrderEdit {...{ isReadOnly, ...rest }} />)
}

interface ModalVariablesOrderReadOnlyProps {
    setOpened: ModalOpenedSetTy
    setCompleteData: React.Dispatch<React.SetStateAction<InputTableLayoutData | null >>
    updateHeaderData: string
    headerData: TwinDictionary
}

const ModalVariablesOrderReadOnly: React.FC<ModalVariablesOrderReadOnlyProps> = ({ headerData, setOpened }) => {
    const {t} = useTwinTranslation()
    const dictVariables = useMemo(() => getSellVariables() || {}, [])
    const currencies = getAllCurrencies()
    const variables = headerData.OrderVariables
    const currencyHeaderId = headerData.CurrencyId
    const columns: ColumnTableSchema<TwinDictionary> = [
        {
            id: 'OrderVariableId',
            dataKey: 'OrderVariableId',
            label: t('variable2', 'Variable'),
            customRender: (value, rowData) => <TextField label='' value={dictVariables?.[rowData.OrderVariableId]?.name} />
        },
        {
            id: 'value',
            dataKey: 'value',
            label: t('description', 'Description'),
            customRender: (value, rowData) => <TextField label='' value={rowData.value || '' + (dictVariables?.[rowData.OrderVariableId].type === SellVariableTypeInversed['price'] ? ' ' + currencies[currencyHeaderId]?.symbolNative : '')} />
        },
    ]
    return (
        <ModalBigCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_variable_sell gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='variables'>Variables</TwinTrans></h2>
            <div className='mt-20 flex flex-auto flex-col'>
                <div className='flex-auto overflow-visible'>
                    <TableWithoutVirtualListing data={variables} columns={columns} className='min-h-300 add_variable_table' />
                </div>
                <div className='flex justify-end gap-x-33'>
                    <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                        <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                    </ButtonSecondary>
                </div>
            </div>
        </ModalBigCentered>
    )
}

interface OrderModelType {
    OrderVariableId: string;
    OrderId: number;
    value?: string;
}

const ModalVariablesOrderEdit: React.FC<ModalVariableProps> = ({ headerData, getCompleteData, orderHeaderURLs, setOpened, setCompleteData }) => {
    const { t } = useTwinTranslation()
    const variables = headerData.OrderVariables
    const { deleteRow, myHandleAdd, myHandleVariable, dictVariables } = useModalVariablesEditLogic({getCompleteData, headerData, orderHeaderURLs, setCompleteData})
    const currencyHeaderId = headerData.CurrencyId
    const keysDictLength = Object.keys(dictVariables)?.length
    const idsOrderVariables = useMemo(() => variables?.map((item: OrderModelType) => String(item.OrderVariableId)), [variables])
    const dictComponents: Record<SellVariableTypeKeys, React.FC<any>> = {
        0: InputNumberWithLabelMarginTextRightDebounce,
        1: InputWithLabelMargin,
        2: InputPrice,
        3: InputWithLabelMargin
    }
    const columns: ColumnTableSchema<TwinDictionary> = [
        {
            id: 'OrderVariableId',
            dataKey: 'OrderVariableId',
            label: t('variable2', 'Variable'),
            customRender: (value, rowData) => <TextField label='' value={dictVariables?.[rowData.OrderVariableId]?.name} />
        },
        {
            id: 'value',
            dataKey: 'value',
            label: t('description', 'Description'),
            customRender: (value, rowData) => {
                let myExtraData: TwinDictionary = {}
                if (dictVariables?.[rowData.OrderVariableId]?.type === SellVariableTypeInversed['selector']) {
                    if (!rowData.value) {
                        myExtraData.items = dictVariables[rowData.OrderVariableId].extraDataParsed
                        myExtraData.component = CustomSelectWithSearchBar
                    }
                }
                const Component = myExtraData.component || dictComponents?.[dictVariables?.[rowData?.OrderVariableId]?.type] || InputNumberWithLabelMarginTextRightDebounce
                return <Component label='' value={rowData?.value || ''} textRight='' classNameParent='w-full' className='w-full' CurrencyId={currencyHeaderId} {...myExtraData} onChange={(value: any) => myHandleVariable(String(rowData.rowIndex), 'value', value)} /> 
            }
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + allRowData.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteRow(allRowData)} />,
        }
    ]

    const sections: FormRender<any> = []
    if (keysDictLength && keysDictLength !== idsOrderVariables?.length) {
        sections.push({
            cols: 1,
            elements: [
                {
                    name: 'add',
                    component: 'CustomSelectSearchBarWithNotInFilteredItems',
                    label: t('addVariable', 'Añadir variable'),
                    items: dictVariables,
                    className: 'items-start w-200 overflow-visible',
                    onChange: (value) => myHandleAdd(headerData?.['id'], 'add', value),
                    excludeItems: idsOrderVariables,
                },
            ]
        })
    }
    return (
        <ModalBigCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_variable_sell gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='addEditVariables'>Añadir o editar variables</TwinTrans></h2>
            <div className='flex flex-auto flex-col'>
                <div className='flex flex-col flex-auto gap-y-20 overflow-visible'>
                    <FormRenderer sections={sections} />
                    <TableWithoutVirtualListing data={variables} columns={columns} className='min-h-300 add_variable_table' />
                </div>
                <div className='flex justify-end gap-x-33'>
                    <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                        <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                    </ButtonSecondary>
                </div>
            </div>
        </ModalBigCentered>
    )
}

interface ModalVariablesEditLogicProps {
    setCompleteData: SetCompleteDataTy
    orderHeaderURLs: OrderHeaderURLs
    headerData: TwinDictionary
    getCompleteData: () => void
}

const useModalVariablesEditLogic = ({ headerData, orderHeaderURLs, setCompleteData, getCompleteData }: ModalVariablesEditLogicProps) => {
    const dictVariables = useMemo(() => getSellVariables() || {}, [])
    
    const myHandleAdd: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        await twinFetchPostJSON(orderHeaderURLs.createVariable, {
            OrderId: String(id),
            OrderVariableId: value,
        })
        getCompleteData()
    }, [orderHeaderURLs, getCompleteData])

    const deleteRow = useCallback(async (variable: any) => {
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            const index = variable.rowIndex
            myData.header.OrderVariables.splice(index, 1)
            return { ...myData }
        })
        const result = await twinFetchPostJSON(orderHeaderURLs.deleteVariable, { OrderId: variable?.OrderId, OrderVariableId: variable?.OrderVariableId })
        if (result) {
            getCompleteData()
        }
    }, [setCompleteData, getCompleteData, orderHeaderURLs.deleteVariable])

    const myHandleVariable: InputTableLeftColumnChange = useCallback(async (index, field, value) => {
        let relatedProducts: any[] = []
        let OrderVariableId = ''
        await flushSync(async () => {
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const copyOld = JSON.parse(JSON.stringify(old))
                OrderVariableId = copyOld.header.OrderVariables[index]?.OrderVariableId
                if (!copyOld.header.OrderVariables[index].value && dictVariables?.[OrderVariableId]?.type === SellVariableTypeInversed['selector']) {
                    value = dictVariables?.[OrderVariableId]?.extraDataParsed?.[value]?.name
                }
                copyOld.header.OrderVariables[index] = {
                    ...copyOld.header.OrderVariables[index], value: value,
                }
                relatedProducts = updateVariableQuantityScaled(copyOld, OrderVariableId, value)
                if (relatedProducts?.length) {
                    recalculateAllQuoteOrderRows(copyOld)
                }
                return copyOld
            })
            await twinFetchPostText(orderHeaderURLs.updateVariable, {
                OrderId: headerData?.id,
                OrderVariableId,
                value: value,
                relatedProducts
            })
        })
    }, [setCompleteData, orderHeaderURLs, dictVariables, headerData?.id])
    
    return { deleteRow, myHandleAdd, myHandleVariable, dictVariables }
}


export default ModalVariablesOrder